import { Button, Card, Stack, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import PaiTooltip from 'features/ui/base-components/pai-tooltip';
import useBrowser from 'hooks/use-browser';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ProductMappedColors, Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';

interface Props {
  productGroup?: ProductGroups;

  featureName: string;
  descriptionPrimary: string;
  descriptionSecondary: string;
  features: string[];

  linkedinRequired?: boolean;
  hot?: boolean;

  disabled?: boolean;
  onBuy?: () => void;

  action?(): void;

  /** Optional custom CTA text for the inactive (unlicensed) state */
  inactiveCtaText?: string;

  /** Optional custom CTA text for the active state */
  ctaText?: string;
}

/** Helle Variante der Akzentfarbe je Produktgruppe (Bullet-Kreise / Badge-Hintergrund). */
const ProductMappedSoftColors: Record<string, string> = {
  default: '#EDECFF',
  [ProductGroups.HR_BOOSTER]: '#fbe6c3',
  [ProductGroups.LEAD_BOOSTER]: '#ffd7e6',
};

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden={true} focusable={false}>
    <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

const InfoIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden={true} focusable={false}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
  </svg>
);

const FeatureItemAdvanced: React.FC<Props> = ({
  productGroup,
  featureName,
  descriptionPrimary,
  descriptionSecondary,
  features,
  linkedinRequired,
  hot,
  disabled,
  onBuy,
  action,
  inactiveCtaText,
  ctaText,
}) => {
  const color = ProductMappedColors[productGroup || 'default'];
  const softColor = ProductMappedSoftColors[productGroup || 'default'];
  const { classes, cx } = useStyles({ color, softColor });
  const { t } = useTranslation();

  const { extensionStoreURL } = useBrowser();

  const openExtensionPage = () => {
    window.open(extensionStoreURL, '_blank');
  };

  return (
    <PaiTooltip title={disabled ? t('noValidLicense') : false}>
      <Card className={cx(classes.productCard, disabled ? 'disabled' : null)}>
        <div className={classes.accentBar} />
        <div className={classes.content}>
          <div className={classes.titleRow}>
            <div className={classes.titleColumn}>
              <Typography className={classes.featureName}>{featureName}</Typography>
              <Typography className={classes.descriptionPrimary}>{descriptionPrimary}</Typography>
              <Typography className={classes.descriptionSecondary}>{descriptionSecondary}</Typography>
            </div>
            <div className={classes.badges}>
              {hot ? <span className={cx(classes.badge, 'hot')}>{t('popular')}</span> : null}
              {disabled ? <span className={cx(classes.badge, 'locked')}>{t('noLicense')}</span> : null}
            </div>
          </div>
          <Stack className={classes.features}>
            {features.map((feature) => (
              <div key={feature} className={classes.featureRow}>
                <span className={classes.bullet}>
                  <CheckIcon className={classes.bulletIcon} />
                </span>
                <Typography className={classes.feature}>{feature}</Typography>
              </div>
            ))}
            {linkedinRequired ? (
              <div
                className={cx(classes.featureRow, 'required')}
                role="button"
                tabIndex={0}
                onClick={openExtensionPage}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openExtensionPage();
                  }
                }}
              >
                <span className={cx(classes.bullet, 'required')}>
                  <InfoIcon className={cx(classes.bulletIcon, 'required')} />
                </span>
                <Typography className={classes.extensionHint}>{t('extensionRequired')}</Typography>
              </div>
            ) : null}
          </Stack>
          <div className={classes.actions}>
            {disabled ? (
              <Button className={cx(classes.cardAction, 'inactive')} disableRipple={true} disableElevation={true} onClick={onBuy}>
                {inactiveCtaText || t('unlockProduct')}
              </Button>
            ) : (
              <Button className={classes.cardAction} disableRipple={true} disableElevation={true} onClick={action}>
                {ctaText || t('toTheFeature')}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </PaiTooltip>
  );
};

const useStyles = makeStyles<{ color: string; softColor: string }>()((theme, { color, softColor }) => ({
  productCard: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '420px',
    maxWidth: '100%',
    background: '#FFF',
    borderRadius: '12px',
    border: '1px solid rgba(0,0,0,0.08)',
    overflow: 'hidden',
    boxShadow: 'none',
    transition: 'box-shadow 150ms ease',
    '&:hover': {
      boxShadow: Shadows.GREY_SHADOW_LIGHTER,
    },
    '&.disabled': {
      border: `1px solid ${theme.palette.greys.lighter}`,
    },
  },
  accentBar: {
    height: '4px',
    background: color,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    flex: 1,
    padding: '24px 24px 20px 24px',
  },
  titleRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    alignItems: 'flex-start',
  },
  titleColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1,
    minWidth: 0,
  },
  featureName: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(22),
    lineHeight: 1.25,
    whiteSpace: 'pre-wrap',
  },
  descriptionPrimary: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(14),
  },
  descriptionSecondary: {
    color: theme.palette.greys.dark,
    fontSize: theme.typography.pxToRem(14),
    textWrap: 'pretty',
  },
  badges: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: '8px',
  },
  badge: {
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 700,
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    borderRadius: '6px',
    padding: '5px 8px',
    whiteSpace: 'nowrap',
    '&.hot': {
      color: theme.palette.warning.main,
      background: theme.palette.backgrounds.lightOrange,
    },
    '&.locked': {
      color: theme.palette.greys.medium,
      background: theme.palette.greys.lighter,
    },
  },
  features: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  featureRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '10px',
    '&.required': {
      cursor: 'pointer',
    },
  },
  bullet: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    background: softColor,
    color: color,
    '&.required': {
      background: theme.palette.backgrounds.main,
      color: theme.palette.primary.main,
    },
  },
  bulletIcon: {
    width: '12px',
    height: '12px',
  },
  feature: {
    color: theme.palette.text.primary,
    fontSize: theme.typography.pxToRem(13),
  },
  extensionHint: {
    color: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(13),
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
    cursor: 'pointer',
  },
  actions: {
    display: 'flex',
    marginTop: 'auto',
    paddingTop: '8px',
  },
  cardAction: {
    width: '100%',
    height: '44px',
    borderRadius: '8px',
    fontSize: theme.typography.pxToRem(16),
    textTransform: 'none',
    color: color,
    background: '#FFF',
    border: '1px solid rgba(0,0,0,0.12)',
    '&:hover': {
      background: '#FFF',
      opacity: 0.85,
    },
    '&.inactive': {
      color: '#FFF',
      background: color,
      border: 'none',
      '&:hover': {
        background: color,
        opacity: 0.85,
      },
    },
  },
}));

export default FeatureItemAdvanced;
